import React, { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container, Box, Button } from '@mui/material'
import { Map as ImmutableMap } from 'immutable'
import { useSelector } from 'react-redux'
import { getAuthPermissions, PERM_DELETE_CONTENT, PERM_UPDATE_CONTENT } from 'services/auth'
import { selectAuth } from 'services/auth/redux/selectors'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { getIdFromV2Location, removeContent } from 'services/content'
import { selectLocation } from 'services/location/redux/selectors'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ConfirmDialog from 'gui/components/common/ConfirmDialog'
import ContentDetailQueryStates from './ContentDetailQueryStates'
import VideoContentInformationSection from './VideoContentInformationSection'
import VideoContentSeoSection from './VideoContentSeoSection'
import VideoContentReferencesSection from './VideoContentReferencesSection'
import VideoContentReferencesExtendedSection from './VideoContentReferencesExtendedSection'
import VideoContentReferencesYoreSection from './VideoContentReferencesYoreSection'
import AIMetadataGeneration from './ai-metadata-generation'
import AIMarketingPlanGeneration from './ai-marketing-plan-generation'
import AITranscript from './ai-transcript'
import { GET_VIDEO_CONTENT, UPDATE_VIDEO_CONTENT } from './queries'
import ContentMediaSection from './content-detail-media/content-media-section'
import ContentImagesSection from './content-detail-images/content-images-section'
import FloatingSaveCancelButtons from './FloatingSaveCancelButtons'
import ContentDetailSnackbar, { useContentDetailSnackbar } from './ContentDetailSnackbar'
import { useSectionManagedSlot } from './useSectionManagedSlot'
import type { ContentInfoChanges } from './types'
import './course-detail.scss'

const SEO_FIELD_NAMES = new Set(['seoTitle', 'seoDescription', 'seoKeywords'])

/**
 * Video Detail V2 Component
 * GraphQL-powered detail page for video (and other) content at /v2/content/:id.
 * Includes Content Information and Content References sections.
 */
function VideoDetailV2 () {
  const location = useSelector(selectLocation)
  const auth = useSelector(selectAuth)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)
  const canDelete = authPermissions.includes(PERM_DELETE_CONTENT)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const id = getIdFromV2Location(location)

  const { loading, error, data, refetch } = useQuery(GET_VIDEO_CONTENT, {
    variables: { id },
    skip: !id,
  })

  const videoData = data?.contentList?.content?.[0] ?? null

  // Parent-managed: section reports changes up, parent owns mutation
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)
  const [mediaDirty, setMediaDirty] = useState<boolean>(false)
  const [mediaChanges, setMediaChanges] = useState<ContentInfoChanges | null>(null)
  const [seoDirty, setSeoDirty] = useState<boolean>(false)
  const [seoChanges, setSeoChanges] = useState<ContentInfoChanges | null>(null)

  // Section-managed: sections own save; useSectionManagedSlot bundles dirty + saveHandlerRef per section
  const contentRefs = useSectionManagedSlot()
  const contentRefsExtended = useSectionManagedSlot()
  const contentRefsYore = useSectionManagedSlot()

  const [resetTrigger, setResetTrigger] = useState<number>(0)
  const [aiInjectedMetadataInfo, setAiInjectedMetadataInfo] = useState<Record<string, unknown>>({})
  const [aiInjectedMetadataSeo, setAiInjectedMetadataSeo] = useState<Record<string, unknown>>({})
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)
  const isDirty =
    contentInfoDirty ||
    mediaDirty ||
    seoDirty ||
    contentRefs.dirty ||
    contentRefsExtended.dirty ||
    contentRefsYore.dirty

  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  const handleMediaDirtyChange = useCallback((isMediaDirty: boolean) => {
    setMediaDirty(isMediaDirty)
  }, [])

  const handleMediaChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setMediaChanges(changes)
  }, [])

  const handleSeoDirtyChange = useCallback((isSeoDirty: boolean) => {
    setSeoDirty(isSeoDirty)
  }, [])

  const handleSeoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setSeoChanges(changes)
  }, [])

  const [updateVideoContent, { loading: saving }] = useMutation(UPDATE_VIDEO_CONTENT, {
    refetchQueries: [{ query: GET_VIDEO_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useContentDetailSnackbar()

  const handleSave = async () => {
    try {
      if (
        !contentInfoChanges &&
        !mediaChanges &&
        !seoChanges &&
        !contentRefs.dirty &&
        !contentRefsExtended.dirty &&
        !contentRefsYore.dirty
      ) {
        showSnackbar('No changes to save', 'info')
        return
      }

      let combinedChanges: ContentInfoChanges | null = null
      const parentManagedChanges = [contentInfoChanges, mediaChanges, seoChanges].filter(
        Boolean,
      ) as ContentInfoChanges[]
      if (parentManagedChanges.length > 0) {
        combinedChanges = {
          contentInput: Object.assign({}, ...parentManagedChanges.map((c) => c.contentInput)),
          generatePath: parentManagedChanges.some((c) => c.generatePath),
        }
      }

      if (combinedChanges) {
        await updateVideoContent({
          variables: {
            content: combinedChanges.contentInput,
            generatePath: combinedChanges.generatePath ?? false,
          },
        })
      }

      if (contentRefs.dirty && contentRefs.saveHandlerRef.current) {
        await contentRefs.saveHandlerRef.current()
      }

      if (contentRefsExtended.dirty && contentRefsExtended.saveHandlerRef.current) {
        await contentRefsExtended.saveHandlerRef.current()
      }

      if (contentRefsYore.dirty && contentRefsYore.saveHandlerRef.current) {
        await contentRefsYore.saveHandlerRef.current()
      }

      await refetch()
      setAiInjectedMetadataInfo({})
      setAiInjectedMetadataSeo({})
      showSnackbar('Content saved successfully!', 'success')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      showSnackbar(`Error saving: ${errorMessage}`, 'error')
    }
  }

  const handleCancel = () => {
    setAiInjectedMetadataInfo({})
    setAiInjectedMetadataSeo({})
    setResetTrigger((prev) => prev + 1)
    showSnackbar('Changes discarded', 'info')
  }

  /** Adapter so AI components (expecting Immutable Map) can read from GraphQL videoData */
  const contentForAiTools = useMemo(
    () => ImmutableMap((videoData ?? {}) as Record<string, unknown>),
    [videoData],
  )

  const setFieldValueFromAi = useCallback((field: string, value: unknown) => {
    if (SEO_FIELD_NAMES.has(field)) {
      setAiInjectedMetadataSeo((prev) => ({ ...prev, [field]: value }))
    } else {
      setAiInjectedMetadataInfo((prev) => ({ ...prev, [field]: value }))
    }
  }, [])

  const openDeleteConfirm = useCallback(() => setDeleteConfirmOpen(true), [])
  const closeDeleteConfirm = useCallback(() => setDeleteConfirmOpen(false), [])

  const handleDeleteConfirm = useCallback(async () => {
    const contentIdForDelete = videoData?.id ?? id
    if (contentIdForDelete == null) return
    await removeContent({
      auth,
      server: brooklynUrl,
      contentId: String(contentIdForDelete),
    })
    window.location.replace('/content')
  }, [auth, brooklynUrl, videoData?.id, id])

  return (
    <ContentDetailQueryStates
      loading={loading}
      error={error}
      hasData={!!videoData}
      errorTitle="Error loading content"
      notFoundTitle="Content not found"
      notFoundDetail={`No content found with ID: ${id}`}
    >
      <Container className="course-detail content-detail" maxWidth={false}>
        <Container className="content-detail__section content-information" maxWidth={false}>
          <VideoContentInformationSection
            contentData={videoData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleContentInfoDirtyChange}
            onChangesUpdate={handleContentInfoChangesUpdate}
            onResetTriggered={resetTrigger}
            aiInjectedMetadata={
              Object.keys(aiInjectedMetadataInfo).length > 0 ? aiInjectedMetadataInfo : undefined
            }
          />
        </Container>

        <Container className="content-detail__section ai-tools" maxWidth={false}>
          <SectionAccordion title="✨ AI Tools" collapsed={true}>
            <AITranscript
              content={contentForAiTools}
              isViewOnlyMode={isViewOnlyMode}
              featureMediaId={videoData?.featureMediaId}
            />
            <Box sx={{ my: 4, borderBottom: '2px solid #e0e0e0' }} />
            <AIMetadataGeneration
              content={contentForAiTools}
              isViewOnlyMode={isViewOnlyMode}
              featureMediaId={videoData?.featureMediaId}
              setFieldValue={setFieldValueFromAi}
            />
            <Box sx={{ my: 4, borderBottom: '2px solid #e0e0e0' }} />
            <AIMarketingPlanGeneration
              content={contentForAiTools}
              isViewOnlyMode={isViewOnlyMode}
              featureMediaId={videoData?.featureMediaId}
            />
          </SectionAccordion>
        </Container>

        <VideoContentReferencesSection
          contentData={videoData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefs.onDirtyChange}
          saveHandlerRef={contentRefs.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <VideoContentReferencesExtendedSection
          contentData={videoData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsExtended.onDirtyChange}
          saveHandlerRef={contentRefsExtended.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <VideoContentReferencesYoreSection
          contentData={videoData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsYore.onDirtyChange}
          saveHandlerRef={contentRefsYore.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <Container className="content-detail__section media-information" maxWidth={false}>
          <SectionAccordion title="Media Information" collapsed={false}>
            <ContentMediaSection
              contentData={videoData}
              isViewOnly={isViewOnlyMode}
              onDirtyChange={handleMediaDirtyChange}
              onChangesUpdate={handleMediaChangesUpdate}
              onResetTriggered={resetTrigger}
            />
          </SectionAccordion>
        </Container>

        <Container className="content-detail__section content-images" maxWidth={false}>
          <SectionAccordion title="Images" collapsed={false}>
            <ContentImagesSection
              contentId={videoData?.contentId}
              contentType={videoData?.contentType}
              language={videoData?.language}
              primaryLanguage={videoData?.primaryLanguage}
            />
          </SectionAccordion>
        </Container>

        <Container className="content-detail__section seo" maxWidth={false}>
          <VideoContentSeoSection
            contentData={videoData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleSeoDirtyChange}
            onChangesUpdate={handleSeoChangesUpdate}
            onResetTriggered={resetTrigger}
            aiInjectedMetadata={
              Object.keys(aiInjectedMetadataSeo).length > 0 ? aiInjectedMetadataSeo : undefined
            }
          />
        </Container>

        <Container
          className="content-detail__section content-actions-delete-section"
          maxWidth={false}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="error"
              onClick={openDeleteConfirm}
              disabled={isViewOnlyMode || saving || !canDelete || !videoData}
              sx={{ minWidth: '120px', height: '36px', fontSize: '14px' }}
            >
              Delete
            </Button>
          </Box>
        </Container>

        {!isViewOnlyMode && (
          <FloatingSaveCancelButtons
            visible={isDirty}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )}

        <ContentDetailSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        />

        <ConfirmDialog
          open={deleteConfirmOpen}
          onClose={closeDeleteConfirm}
          title="Delete Content"
          description={`Are you sure you want to delete "${(videoData?.title ?? '').trim() || 'this content'}"?`}
          onConfirm={handleDeleteConfirm}
          confirmLabel="Delete"
          busyLabel="Deleting..."
          severity="error"
        />
      </Container>
    </ContentDetailQueryStates>
  )
}

export default VideoDetailV2
